import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AvatarIcon, CountryFlag, COUNTRY_NAME } from "@/lib/identity";
import { Crown, Trophy } from "@phosphor-icons/react";

export default function HallOfFame() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/season/hall-of-fame").then((r) => setData(r.data)).catch(() => setData({ national_ng: [], national_gh: [], international: [] }));
  }, []);

  if (!data) return <div className="text-nk-mute text-sm">Loading…</div>;
  const ng = data.national_ng || [];
  const gh = data.national_gh || [];
  const intl = data.international || [];
  const leaders = data.country_leaders || [];
  // Hide NG/GH country-leader rows because they get the bigger "National Champion" billing.
  const otherLeaders = leaders.filter((d) => !["NG", "GH"].includes(d.country));
  const empty = ng.length === 0 && gh.length === 0 && intl.length === 0 && otherLeaders.length === 0;

  return (
    <div className="space-y-10 max-w-5xl">
      <header>
        <p className="eyebrow mb-2">Immortalized</p>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Hall of Fame</h1>
        <p className="text-nk-mute text-sm mt-3 max-w-xl">
          Every season closes with new entries here: National Champions for Nigeria and Ghana, the International Champion, and Country Leaders for every other country. Once you're in, you're in for life.
        </p>
      </header>

      {empty && (
        <div className="nk-card p-10 text-center space-y-3">
          <Trophy size={28} weight="duotone" className="text-nk-gold mx-auto" />
          <p className="font-display text-xl">No champions yet.</p>
          <p className="text-sm text-nk-mute">Season 1 closes in a few weeks — be the first.</p>
        </div>
      )}

      <Section title="International Champions" icon={<Crown size={18} weight="fill" className="text-nk-gold" />} entries={intl} highlight gold />

      <Section title="Nigeria · National Champions" icon={<CountryFlag country="NG" size={16} />} entries={ng} />

      <Section title="Ghana · National Champions" icon={<CountryFlag country="GH" size={16} />} entries={gh} />

      {otherLeaders.length > 0 && (
        <Section
          title="Country Leaders"
          icon={<Crown size={16} weight="duotone" className="text-nk-gold" />}
          entries={otherLeaders}
          subtitle="The #1 player in each country at season end. Recognition + permanent title — cash prizes unlock as the league grows."
        />
      )}

      <div className="text-xs text-nk-dim border-t border-nk-line/40 pt-6">
        Champions receive prize money disbursed directly by NKognito (no wallets, no escrow). Payout details
        are collected via the Profile page once you've claimed a championship.
      </div>
    </div>
  );
}

function Section({ title, icon, entries, gold = false, highlight = false, subtitle = null }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <p className={`eyebrow ${gold ? "text-nk-gold" : ""}`}>{title}</p>
        <span className="text-xs text-nk-dim font-mono ml-2">{entries.length}</span>
      </div>
      {subtitle && <p className="text-xs text-nk-mute mb-3 max-w-xl">{subtitle}</p>}
      {entries.length === 0 ? (
        <div className="nk-card p-5 text-sm text-nk-dim">No entries yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {entries.map((e, i) => (
            <Link
              key={`${e.season_number}-${e.user_id}-${i}`}
              to={`/profile/${e.user_id}`}
              className={`nk-card p-4 flex items-center gap-4 hover:border-nk-accent transition-colors ${
                highlight ? "border-nk-gold/40" : ""
              }`}
            >
              <AvatarIcon id={e.avatar} size={42} />
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate flex items-center gap-2">
                  {e.username} <CountryFlag country={e.country} size={12} />
                </p>
                <p className="text-xs text-nk-mute mt-0.5 font-mono">
                  Season {e.season_number}{e.country_name ? ` · ${e.country_name}` : ""} · {(e.season_score || 0).toLocaleString()} SS
                </p>
              </div>
              {gold && <Crown size={16} weight="fill" className="text-nk-gold shrink-0" />}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
